import { useWeddingEvents } from '@bridebook/web/lib/wedding-website/hooks/query';
import { compact } from 'lodash';
import { useMemo } from 'react';
import { sortEvents } from './sort-unified-events';
import { Event, eventTypeMap, isCustomEvent, isMainEvent } from './types';
import { useMainEvent } from './use-main-event';
import { getCustomEvents } from './utils';

/**
 * Hook that unifies all wedding events into a consistent format.
 * Returns main event and custom events with the same simplified type structure.
 *
 * For the main event:
 * - If wedding date and venue supplier exist: Returns enhanced main event with those wedding details
 * - If date or supplier missing: Returns unified main event with empty strings for those fields
 *
 * All events are sorted by date when possible.
 */

export const useUnifiedEvents = () => {
  const { data: customEvents, isLoading } = useWeddingEvents(getCustomEvents);
  const { mainEventWithWeddingData, mainEvent } = useMainEvent();

  const unifiedCustomEvents: Event[] = useMemo(() => {
    const customEventsTyped = customEvents?.filter(isCustomEvent);
    if (!customEventsTyped) return [];
    return customEventsTyped?.map((event) => ({
      ...event,
      type: eventTypeMap.CUSTOM_EVENT,
    }));
  }, [customEvents]);

  const mainEventToAdd: Event | null = useMemo(() => {
    if (!mainEventWithWeddingData && mainEvent && isMainEvent(mainEvent)) {
      // if unifiedMainEvent doesnt exist, that means we don't have the date and/or supplier.
      // need to manually add those empty fields to the main event
      return {
        ...mainEvent,
        type: eventTypeMap.MAIN_EVENT,
        date: '',
        supplier: '',
      };
    }
    return mainEventWithWeddingData;
  }, [mainEvent, mainEventWithWeddingData]);

  const unifiedEvents: Event[] = useMemo(() => {
    if (mainEventWithWeddingData) {
      // main event is enhanced - we have date and supplier. We can sort all together
      const events = compact([mainEventWithWeddingData, ...unifiedCustomEvents]);
      return sortEvents(events);
    }
    // main event is not enhanced = we don't have date and/or supplier. We need to add it to the custom events at the beginning
    const events = compact([mainEventToAdd, ...sortEvents(unifiedCustomEvents)]);
    return events;
  }, [mainEventToAdd, unifiedCustomEvents, mainEventWithWeddingData]);

  return { unifiedCustomEvents, unifiedEvents, isLoading };
};
